import { useId, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router';
import { m } from 'motion/react';
import { ChevronsUpDown, LogOut, Monitor, Moon, MoreHorizontal, Sun } from 'lucide-react';
import { cx, initials } from '../lib/format';
import { useSession } from '../lib/session';
import { useGlass, useTheme, type ThemePref } from '../lib/theme';
import { Sheet } from '../components/ui/Overlay';
import { Segmented, Switch } from '../components/ui/Field';
import { Popover } from '../components/ui/Layout';

export { PlatformMark as BrandMark, CompanyAvatar as Avatar } from '../components/Brand';

export interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  end?: boolean;
  badge?: number;
  /** Aparece na barra inferior do celular. */
  primary?: boolean;
  /** Rótulo curto para a barra inferior. */
  short?: string;
}

const spring = { type: 'spring', stiffness: 520, damping: 42, mass: 0.8 } as const;

/** Preferências de aparência: tema e transparência (Liquid Glass ou superfícies opacas). */
export function AppearanceControls() {
  const { pref, setPref } = useTheme();
  const { glass, setGlass } = useGlass();
  return (
    <div className="space-y-3 p-1.5">
      <div>
        <div className="eyebrow mb-1.5 px-0.5">Aparência</div>
        <Segmented<ThemePref>
          size="sm"
          ariaLabel="Tema"
          value={pref}
          onChange={setPref}
          className="w-full [&>button]:flex-1 [&>button]:justify-center"
          options={[
            { value: 'light', label: <><Sun aria-hidden />Claro</> },
            { value: 'system', label: <><Monitor aria-hidden />Auto</> },
            { value: 'dark', label: <><Moon aria-hidden />Escuro</> },
          ]}
        />
      </div>
      <Switch checked={glass === 'off'} onChange={(v) => setGlass(v ? 'off' : 'auto')} label="Reduzir transparência" description="Usa superfícies opacas na navegação." />
    </div>
  );
}

function UserMenu({ compact }: { compact?: boolean }) {
  const { me, logout } = useSession();
  if (!me) return null;
  return (
    <Popover
      align={compact ? 'right' : 'left'}
      className={cx('w-[280px]', !compact && 'bottom-full mb-2 mt-0')}
      trigger={({ toggle, open }) => (
        <button type="button" onClick={toggle} aria-expanded={open} aria-haspopup="menu" aria-label="Conta e aparência" className={cx('flex items-center gap-2.5 rounded-[14px] text-left transition-colors hover:bg-hover', compact ? 'p-1' : 'w-full p-2')}>
          <span className="grid size-8 shrink-0 place-items-center rounded-full bg-sunken text-[12px] font-semibold text-ink-2 shadow-[inset_0_0_0_1px_var(--separator)]" aria-hidden>{initials(me.user.name)}</span>
          {!compact && (
            <>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-[560]">{me.user.name}</span>
                <span className="block truncate text-[11.5px] text-ink-3">{me.user.email}</span>
              </span>
              <ChevronsUpDown className="size-4 shrink-0 text-ink-3" aria-hidden />
            </>
          )}
        </button>
      )}
    >
      {() => (
        <div>
          <div className="border-b border-separator px-2.5 pb-2.5 pt-1.5">
            <div className="truncate text-[13.5px] font-[600]">{me.user.name}</div>
            <div className="truncate text-[12px] text-ink-3">{me.user.email}</div>
          </div>
          <AppearanceControls />
          <div className="border-t border-separator pt-1.5">
            <button type="button" role="menuitem" onClick={() => void logout()} className="flex min-h-10 w-full items-center gap-2.5 rounded-[12px] px-2.5 text-left text-[13.5px] text-danger hover:bg-hover coarse:min-h-11">
              <LogOut className="size-[18px]" aria-hidden /> Sair
            </button>
          </div>
        </div>
      )}
    </Popover>
  );
}

function NavRow({ item, onNavigate, group }: { item: NavItem; onNavigate?: () => void; group: string }) {
  return (
    <NavLink to={item.to} end={item.end} onClick={onNavigate} className={({ isActive }) => cx('group relative flex h-10 items-center gap-3 rounded-[12px] px-3 text-[14px] font-[540] outline-offset-[-2px] transition-colors coarse:h-11', isActive ? 'text-primary-text' : 'text-ink-2 hover:bg-hover hover:text-ink')}>
      {({ isActive }) => (
        <>
          {isActive && <m.span layoutId={`nav-active-${group}`} className="absolute inset-0 rounded-[12px] bg-primary-soft shadow-[inset_0_0_0_1px_var(--primary-soft)]" transition={spring} aria-hidden />}
          <span className="relative grid size-5 place-items-center [&>svg]:size-5" aria-hidden>{item.icon}</span>
          <span className="relative min-w-0 flex-1 truncate">{item.label}</span>
          {!!item.badge && <span className="num relative min-w-5 rounded-full bg-warning-soft px-1.5 text-center text-[11px] font-semibold text-warning" aria-label={`${item.badge} pendentes`}>{item.badge}</span>}
          {isActive && <span className="sr-only">(página atual)</span>}
        </>
      )}
    </NavLink>
  );
}

interface ShellProps {
  header: ReactNode;
  nav: Array<{ title?: string; items: NavItem[] }>;
  children: ReactNode;
  banner?: ReactNode;
  mobileTitle: ReactNode;
  mobileAction?: ReactNode;
  /** Identifica o ambiente (ex.: "Administração") na lateral. */
  contextLabel?: ReactNode;
}

/**
 * Estrutura comum aos dois ambientes.
 * Desktop: barra lateral flutuante em vidro. Celular: barra superior e barra inferior flutuantes em vidro,
 * com o conteúdo em superfícies opacas e espaço reservado para as barras.
 */
export function AppShell({ header, nav, children, banner, mobileTitle, mobileAction, contextLabel }: ShellProps) {
  const [more, setMore] = useState(false);
  const location = useLocation();
  const group = useId();
  const all = nav.flatMap((s) => s.items);
  const primary = all.filter((i) => i.primary).slice(0, 4);
  const moreActive = !primary.some((i) => (i.end ? location.pathname === i.to : location.pathname.startsWith(i.to)));

  const sections = (onNavigate?: () => void, g = group) =>
    nav.map((section, i) => (
      <div key={i}>
        {section.title && <div className="eyebrow mb-1.5 px-3">{section.title}</div>}
        <div className="space-y-0.5">
          {section.items.map((item) => (
            <NavRow key={item.to} item={item} onNavigate={onNavigate} group={g} />
          ))}
        </div>
      </div>
    ));

  return (
    <div className="min-h-dvh">
      <div className="app-backdrop" aria-hidden />
      <a href="#conteudo" className="sr-only z-[80] rounded-[12px] bg-surface px-4 py-2 shadow-md focus:not-sr-only focus:fixed focus:left-4 focus:top-4">Pular para o conteúdo</a>

      {/* Barra lateral flutuante (desktop) */}
      <aside className="glass fixed bottom-3 left-3 top-3 z-30 hidden w-[264px] flex-col rounded-[24px] px-3 pb-3 pt-4 lg:flex" aria-label="Navegação lateral">
        <div className="mb-3">{header}</div>
        {contextLabel && <div className="mb-3 px-1">{contextLabel}</div>}
        <nav className="-mx-1 flex-1 space-y-5 overflow-y-auto px-1 pt-1" aria-label="Navegação principal">
          {sections()}
        </nav>
        <div className="border-t border-separator pt-2">
          <UserMenu />
        </div>
      </aside>

      {/* Barra superior (celular/tablet) */}
      <header className="glass-flat sticky top-0 z-30 flex min-h-14 items-center gap-3 border-b border-separator px-4 pt-[var(--safe-top)] lg:hidden">
        <div className="min-w-0 flex-1">{mobileTitle}</div>
        {mobileAction}
        <UserMenu compact />
      </header>

      <div className="lg:pl-[288px]">
        {banner && <div className="lg:pr-3 lg:pt-3">{banner}</div>}
        <main id="conteudo" tabIndex={-1} key={location.pathname.split('/').slice(0, 3).join('/')} className="pb-tabbar mx-auto w-full max-w-[1320px] px-4 pt-5 outline-none sm:px-6 lg:px-10 lg:pb-12 lg:pt-8">
          {children}
        </main>
      </div>

      {/* Barra inferior flutuante (celular/tablet) */}
      <nav className="glass fixed inset-x-3 bottom-[max(10px,var(--safe-bottom))] z-30 mx-auto flex h-[var(--tabbar-h)] max-w-[560px] items-stretch rounded-[26px] px-1.5 lg:hidden" aria-label="Navegação">
        {primary.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} className={({ isActive }) => cx('relative flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-[20px] text-[10.5px] font-[580] outline-offset-[-3px]', isActive ? 'text-primary-text' : 'text-ink-2')}>
            {({ isActive }) => (
              <>
                {isActive && <m.span layoutId="tab-active" className="absolute inset-x-0.5 inset-y-1.5 rounded-[18px] bg-primary-soft" transition={spring} aria-hidden />}
                <span className="relative [&>svg]:size-[22px]" aria-hidden>{item.icon}</span>
                <span className="relative max-w-full truncate px-1">{item.short ?? item.label}</span>
                {!!item.badge && <span className="num absolute right-[calc(50%-22px)] top-1.5 min-w-4 rounded-full bg-danger px-1 text-center text-[10px] font-semibold text-white" aria-label={`${item.badge} pendentes`}>{item.badge}</span>}
              </>
            )}
          </NavLink>
        ))}
        <button type="button" onClick={() => setMore(true)} aria-haspopup="dialog" className={cx('relative flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-[20px] text-[10.5px] font-[580]', moreActive ? 'text-primary-text' : 'text-ink-2')}>
          {moreActive && <m.span layoutId="tab-active" className="absolute inset-x-0.5 inset-y-1.5 rounded-[18px] bg-primary-soft" transition={spring} aria-hidden />}
          <MoreHorizontal className="relative size-[22px]" aria-hidden />
          <span className="relative">Mais</span>
        </button>
      </nav>

      <Sheet open={more} onClose={() => setMore(false)} title="Menu">
        <div className="mb-4">{header}</div>
        <div className="space-y-5">{sections(() => setMore(false), `${group}-sheet`)}</div>
      </Sheet>
    </div>
  );
}
